import React, { useRef, useState, useEffect, useCallback } from 'react';

import { colors, Loader } from '@crehana/ui';
import TextArea from '@crehana/ui/dist/TextField/TextArea';

import { Smile, PaperPlaneOutline } from '@crehana/icons';

import Loadable from 'Shared/Loadable';

import { useCrehanaLiveTranslations } from 'Jsx/CrehanaLive/hooks';

interface TextFieldFormProps {
  replyUser?: string;
  setReplyUser: (username?: string) => void;
  onSubmit(message: string): void;
}

const TextFieldForm: React.FC<TextFieldFormProps> = ({
  replyUser,
  setReplyUser,
  onSubmit: onSubmitProp,
}) => {
  const { t } = useCrehanaLiveTranslations();
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const [inputMessage, setInputMessage] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  useEffect(() => {
    if (replyUser && !inputMessage.includes(replyUser)) {
      setInputMessage(prev => `@${replyUser} ${prev}`);
      if (inputRef.current) {
        inputRef.current.focus();
      }
      if (!inputMessage) {
        setReplyUser(undefined);
      }
    }
  }, [inputMessage, replyUser]);

  const onTextFieldChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputMessage(event.target.value);
  };

  const onSubmit = (event?: React.FormEvent<HTMLFormElement>) => {
    if (event) {
      event.preventDefault();
    }

    if (inputMessage) {
      onSubmitProp(inputMessage);

      setInputMessage('');
    }
  };

  const onSelectEmoji = useCallback(emoji => {
    if (emoji.native) {
      setInputMessage(prevMessage => `${prevMessage}${emoji.native}`);
    } else if (emoji.imageUrl) {
      setInputMessage(
        prevMessage =>
          `${prevMessage}<img src="${emoji.imageUrl}" width="24" height="24" alt="${emoji.name}" />`,
      );
    }

    setShowEmojiPicker(false);
  }, []);

  return (
    <React.Fragment>
      <form onSubmit={onSubmit} className="h-full relative">
        <TextArea
          ref={inputRef}
          className="h-full"
          rows={1}
          rowsMax={1}
          onChange={onTextFieldChange}
          value={inputMessage}
          endAdornment={
            <React.Fragment>
              <button
                className="cursor-pointer p-4 outline-none"
                type="button"
                onClick={() => setShowEmojiPicker(true)}
              >
                <Smile color={colors.secondaryLight} size={24} />
              </button>
              <button className="cursor-pointer p-4 outline-none" type="submit">
                <PaperPlaneOutline
                  color={inputMessage ? colors.secondaryLight : colors.white}
                  size={24}
                />
              </button>
            </React.Fragment>
          }
          inputProps={{
            onKeyDown: (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
              // 13 is the keyCode for Enter
              if (event.keyCode === 13) {
                // If the user press enter + shift just let him create a new line
                if (event.shiftKey) {
                  return undefined;
                }
                // if it is only entered, send the message
                event.preventDefault();
                onSubmit();
                return undefined;
              }
            },
            placeholder: t('WRITE_MESSAGE_HERE'),
          }}
          maxLength={500}
          full
          darkMode
          textType="body4"
        />
        {showEmojiPicker && (
          <Loadable
            Loader={
              <div
                className="flex flex-col items-center justify-center absolute bg-base-dark"
                style={{
                  width: 348,
                  height: 307,
                  maxWidth: 320,
                  right: 0,
                  bottom: '100%',
                }}
              >
                <Loader white />
                <br />
                <p className="font-body3 text-white">{t('LOAD_EMOJIS')}</p>
              </div>
            }
            module={() =>
              import(/* webpackChunkName: "crehana-live-emojis" */ './Emojis')
            }
            onSelect={onSelectEmoji}
            onClickOutside={() => setShowEmojiPicker(false)}
          />
        )}
      </form>
      <p
        className="absolute hidden md:block font-label text-base-lighter"
        style={{
          bottom: 8,
          left: 24,
        }}
      >
        {inputMessage.length}/500
      </p>
    </React.Fragment>
  );
};

export default TextFieldForm;
